import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import APCheckbox from "../../../components/form/APCheckbox";
import { toDollars } from "../../../helpers/currencies";
import {
  getContactCRMUrl,
  getVehicleDealerCRMUrl,
} from "../../../helpers/dossiers";
import { stripSpaces } from "../../../helpers/string";
import {
  VehicleDealer,
  VehicleDealerRatingLabelMap,
} from "../../../types/VehicleDealer";

export type VehicleVariantCardActions = {
  onToggleSelect: (vehicleVariant: VehicleDealer) => Promise<void>;
};

export type VehicleVariantCardConfig = {
  isSelected: boolean;
  hasSentRequest: boolean;
  crmClientURL: string | null;
};

const VehicleDealerCard = ({
  vehicleDealer,
  actions,
  config,
}: {
  vehicleDealer: VehicleDealer;
  actions: VehicleVariantCardActions;
  config: VehicleVariantCardConfig;
}) => {
  const renderToggleSelect = () => {
    return (
      <APCheckbox
        name="selected"
        label=""
        defaultValue={false}
        value={config.isSelected}
        onChange={() => actions.onToggleSelect(vehicleDealer)}
        stopPropogation={true}
      />
    );
  };

  const renderCardRow = (left: React.ReactNode, right: React.ReactNode) => {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box sx={{ flex: 1 }}>{left}</Box>
        <Box sx={{ flex: 1, alignItems: "flex-end", textAlign: "right" }}>
          {right}
        </Box>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        display: "flex",
        backgroundColor: "surface.main",
        borderColor: config.hasSentRequest ? "primary.main" : undefined,
        flexDirection: "column",
      }}
      variant="outlined"
    >
      <CardActionArea onClick={() => actions.onToggleSelect(vehicleDealer)}>
        <Stack
          sx={{
            p: {
              xs: 1,
              sm: 2,
            },
          }}
          spacing={1}
        >
          <Box
            sx={{
              display: "flex",
              flexDirecion: "row",
              justifyContent: "space-between",

              width: "100%",
            }}
            pb={{
              xs: 0,
              sm: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {renderToggleSelect()}
              {config.crmClientURL && (
                <a
                  href={getVehicleDealerCRMUrl(
                    config.crmClientURL,
                    vehicleDealer.id
                  )}
                  onClick={(e) => e.stopPropagation()}
                  target="_blank"
                >
                  <Typography variant="titleLarge" color="primary.main">
                    {vehicleDealer.name}
                  </Typography>
                </a>
              )}
            </Box>
            <Box />
          </Box>

          <Box sx={{ display: "flex", alignSelf: "start", width: "100%" }}>
            <Stack spacing={1} sx={{ width: "100%" }}>
              {renderCardRow(
                <Typography variant="titleMedium" color="onSurface.main">
                  {vehicleDealer.rating
                    ? VehicleDealerRatingLabelMap[vehicleDealer.rating]
                    : ""}
                </Typography>,
                vehicleDealer.phone ? (
                  <a
                    href={`tel:${stripSpaces(vehicleDealer.phone)}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Typography variant="titleMedium" color="primary.main">
                      {vehicleDealer.phone}
                    </Typography>
                  </a>
                ) : (
                  <></>
                )
              )}

              {renderCardRow(
                <Typography variant="titleMedium" color="onSurface.main">
                  Fee:{" "}
                  {vehicleDealer.vehicleSourcingFeeExGST
                    ? toDollars(vehicleDealer.vehicleSourcingFeeExGST, 0)
                    : ""}
                </Typography>,
                vehicleDealer.primaryContactId && config.crmClientURL ? (
                  <a
                    href={getContactCRMUrl(
                      config.crmClientURL,
                      vehicleDealer.primaryContactId
                    )}
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                  >
                    <Typography variant="titleMedium" color="primary.main">
                      {vehicleDealer.primaryContactName}
                    </Typography>
                  </a>
                ) : (
                  <></>
                )
              )}

              {renderCardRow(
                <Typography variant="titleMedium" color="onSurface.main">
                  {vehicleDealer.address ?? vehicleDealer.state}
                </Typography>,
                <Typography variant="titleMedium" color="secondary.main">
                  {config.hasSentRequest ? "Request Sent" : ""}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default VehicleDealerCard;
